import { createTheme, SxProps, Theme } from "@mui/material/styles";
import { gridClasses } from "@mui/x-data-grid-premium";

declare module '@mui/material/styles' {
	interface TypeText {
		tertiary: string;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsSizeOverrides {
		mini: true;
	}
}

const DarkTheme = createTheme({
	palette: {
		mode: 'dark',
		primary: {
			light: '#ff4675', //'#f03262',
			main: '#ff4675',
			dark: '#ff4675',
			contrastText: '#ffffff'
		},
		secondary: {
			light: '#f6831b',
			main: '#f6831b',
			dark: '#f6831b',
			contrastText: '#0a8ce4'
		},
		grey: {
			"100": "#d5d5d5",
			"200": "#c3c3c3",
			"300": "#b1b1b1",
			"400": "#9f9f9f",
			"500": "#8d8d8d",
			"600": "#7a7a7a",
			"700": "#686868",
			"800": "#565656",
			"900": "#151b1d",
			"A100": "#444444",
			"A200": "#323232",
			"A400": "#9f9f9f",
			"A700": "#686868"
		},
		background: {
			paper: '#061019',
			default: '#061019'
		},
		text: {
			tertiary: '#8d8d8d'
		}
	},
	typography: {
		fontFamily: 'Montserrat, system-ui, sans-serif',
	},
	shape: {
		borderRadius: 12
	},
	components: {
		MuiLink: {
			styleOverrides: {
				root: {
					letterSpacing: 0.5,
					textDecoration: 'none',
					transition: 'color 0.2s ease',
					'&:hover': {
						color: '#ff80a0'
					}
				}
			}
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					'&.MuiTypography-body1': {
						lineHeight: 1.6
					}
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: 'filled',
				slotProps: {
					input: {
						style: {
							borderRadius: 12
						}
					}
				}
			}
		},
		MuiButton: {
			defaultProps: {
				variant: 'contained',
			},
			variants: [{
				props: {
					variant: 'contained',
				},
				style: {
					backgroundImage: 'linear-gradient(to right, #a9227e, #f03262, #f6831b)',
				}
			}, {
				props: { variant: 'outlined' },
				style: {
					position: 'relative',
					backgroundImage: 'linear-gradient(to right, #a9227e, #f03262, #f6831b)',
					paddingRight: 16,
					paddingLeft: 16,
					'&::after': {
						content: '""',
						position: 'absolute',
						top: 1,
						right: 1,
						bottom: 1,
						left: 1,
						borderRadius: 11,
						backgroundColor: '#151b1d'
					},
					'>*': {
						zIndex: 1
					}
				}
			}, {
				props: { size: 'mini' },
				style: {
					minWidth: 50,
					padding: '4px 7px',
					fontSize: '0.75rem',
					color: '#686868',
					'&:hover': {
						color: '#ff4675'
					}
				}
			}]
		},
		MuiChip: {
			styleOverrides: {
				root: {
					transition: 'border-color 0.2s ease, color 0.2s ease'
				},
				colorPrimary: {
					borderColor: '#ff4675',
					'&:hover': {
						borderColor: '#ff80a0',
						color: '#ff80a0'
					}
				},
				colorSecondary: {
					borderColor: '#f6831b',
					'&:hover': {
						borderColor: '#ffa049',
						color: '#ffa049'
					}
				}
			}
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					'@media (max-width:600px)': {
						margin: 16, // Apply 16px margin for xs screen size
					}
				}
			}
		},
		MuiDialogContent: {
			defaultProps: {
				dividers: true,
				style: {
					padding: '16px 20px'
				}
			}
		},
		MuiDialogActions: {
			defaultProps: {
				style: {
					padding: '16px 20px'
				}
			}
		},
		MuiCardActions: {
			defaultProps: {
				style: {
					padding: '0 16px 16px 16px'
				}
			}
		}
	}
});

export const dataGridStyles: SxProps<Theme> = {
	border: 'none',
	// Hide the scrollbar on the virtual scroll container
	[`& .${gridClasses.scrollbar}`]: {
		display: 'none'
	},
	[`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
		outline: 'none',
	},
	[`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
		outline: 'none',
	},
	[`& .${gridClasses.columnHeaderTitle}`]: {
		fontWeight: 600
	},
	[`& .${gridClasses.columnSeparator}`]: {
		opacity: 0
	},
	[`& .${gridClasses.row}`]: {
		cursor: 'pointer',
		transition: 'background-color 0.2s ease'
	},
	[`& .${gridClasses.cell}`]: {
		borderTop: 'none'
	},
	[`& .${gridClasses.cell}:nth-of-type(2)`]: {
		display: 'flex',
		alignItems: 'center',
		padding: '0',
		[`>img`]: {
			borderRadius: '8px'
		}
	},
	[`& .${gridClasses.cell}:nth-of-type(3)`]: {
		paddingTop: 1
	},
	[`& .${gridClasses.cell}:nth-of-type(4)`]: {
		paddingTop: 1
	},
	[`& .${gridClasses.cell}:nth-of-type(5)`]: {
		paddingTop: 1
	},
	[`& .MuiDataGrid-pinnedColumnHeaders`]: {
		backgroundImage: 'none',
		boxShadow: 'none'
	},
	[`& .MuiDataGrid-pinnedColumns`]: {
		backgroundImage: 'none',
		boxShadow: 'none',
		borderRightWidth: 1,
		borderRightColor: '#383938',
		borderRightStyle: 'solid'
	},
	[`& .MuiDataGrid-pinnedRows`]: {
		backgroundImage: 'none',
		boxShadow: 'none',
		// borderTopWidth: 1,
		// borderTopStyle: 'solid',
		// borderTopColor: '#383938'
	}
};

export default DarkTheme;
